import { Typography } from '@mui/material';
import { useEffect } from 'react';
import { Label, Pie, PieChart, ResponsiveContainer } from 'recharts';
import {
  useGetObservationMetricMutation,
  useGetIncidentsHighSevereMetricMutation,
  useGetReInspectionFrequencyMetricMutation,
  useGetTasksOnTimeMetricMutation,
} from 'redux/services/dashboard';
import './MajorMetrics.scss';

const RADIUS = 65;
const LABEL_FONT_SIZE = 14;
const MID_LABEL_FONT_SIZE = 20;
const LABEL_TOP = 100;

export type MajorMetricsProps = {
  projectId: string;
  userId: string;
};

export const MajorMetrics: React.FC<MajorMetricsProps> = ({
  projectId,
  userId,
}) => {
  const [getObservationMetrics, { data: obsData }] =
    useGetObservationMetricMutation();
  const [getIncidentMetrics, { data: incidentData }] =
    useGetIncidentsHighSevereMetricMutation();
  const [getReInspectionMetrics, { data: resinspectionData }] =
    useGetReInspectionFrequencyMetricMutation();
  const [getTasksMetrics, { data: tasksData }] =
    useGetTasksOnTimeMetricMutation();

  useEffect(() => {
    if (projectId) {
      getObservationMetrics({ projectId, userId });
      getIncidentMetrics({ projectId, userId });
      getReInspectionMetrics({ projectId, userId });
      getTasksMetrics({ projectId, userId });
    }
  }, [
    projectId,
    userId,
    getObservationMetrics,
    getIncidentMetrics,
    getReInspectionMetrics,
    getTasksMetrics,
  ]);

  const renderPaddingCircle = (cx: number | string, color: string) => {
    return (
      <Pie
        data={[{ value: 100 }]}
        cx={cx}
        innerRadius={RADIUS}
        outerRadius={RADIUS}
        dataKey="value"
        stroke={color}
        strokeWidth={2}
      />
    );
  };
  return (
    <div className="major-metrics d-flex flex-column p-3 overflow-auto">
      <Typography className="chart-title" variant="h6">
        Major Metrics
      </Typography>
      <ResponsiveContainer
        width="99%"
        minHeight={200}
        minWidth={600}
        height="100%"
      >
        <PieChart>
          {renderPaddingCircle('11%', '#439371')}
          <Pie
            data={[{ value: 100 }]}
            cx="11%"
            innerRadius={RADIUS - 5}
            outerRadius={RADIUS + 5}
            startAngle={0}
            endAngle={(360 * (resinspectionData?.percent ?? 0)) / 100}
            fill="#114630"
            dataKey="value"
          >
            <Label
              fill="#000000"
              fontWeight={'bold'}
              fontSize={MID_LABEL_FONT_SIZE}
              position="center"
              value={`${resinspectionData?.percent ?? 0}%`}
            />
            <Label
              fill="#000000"
              fontWeight={500}
              fontSize={LABEL_FONT_SIZE}
              width={12}
              dy={LABEL_TOP}
              position="centerBottom"
              value="Re-inspection frequency"
            />
          </Pie>

          {renderPaddingCircle('37%', '#0A324F')}
          <Pie
            data={[{ value: 100 }]}
            cx="37%"
            innerRadius={RADIUS - 5}
            outerRadius={RADIUS + 5}
            startAngle={0}
            endAngle={(360 * (obsData?.percent ?? 0)) / 100}
            fill="#0B3C60"
            dataKey="value"
          >
            <Label
              fill="#000000"
              fontWeight={'bold'}
              fontSize={MID_LABEL_FONT_SIZE}
              position="center"
              value={`${obsData?.percent ?? 0}%`}
            />
            <Label
              fill="#000000"
              fontWeight={500}
              fontSize={LABEL_FONT_SIZE}
              width={12}
              dy={LABEL_TOP}
              position="centerBottom"
              value="Observations positive"
            />
          </Pie>

          {renderPaddingCircle('63%', '#D35135')}
          <Pie
            data={[{ value: 100 }]}
            cx="63%"
            innerRadius={RADIUS - 5}
            outerRadius={RADIUS + 5}
            startAngle={0}
            endAngle={(360 * (incidentData?.percent ?? 0)) / 100}
            fill="#752513"
            dataKey="value"
          >
            <Label
              fill="#000000"
              fontWeight={'bold'}
              fontSize={MID_LABEL_FONT_SIZE}
              position="center"
              value={`${incidentData?.percent ?? 0}%`}
            />
            <Label
              fill="#000000"
              fontWeight={500}
              fontSize={LABEL_FONT_SIZE}
              dy={LABEL_TOP}
              width={100}
              position="centerBottom"
              value="Incidents are highly severe"
            />
          </Pie>

          {renderPaddingCircle('89%', '#F6B041')}
          <Pie
            data={[{ value: 100 }]}
            cx="89%"
            innerRadius={RADIUS - 5}
            outerRadius={RADIUS + 5}
            startAngle={0}
            endAngle={(360 * (tasksData?.percent ?? 0)) / 100}
            fill="#F8B242"
            dataKey="value"
          >
            <Label
              fill="#000000"
              fontWeight={'bold'}
              fontSize={MID_LABEL_FONT_SIZE}
              position="center"
              value={`${tasksData?.percent ?? 0}%`}
            />
            <Label
              fill="#000000"
              fontWeight={500}
              fontSize={LABEL_FONT_SIZE}
              dy={LABEL_TOP}
              width={100}
              position="centerBottom"
              value="Tasks solved on Time"
            />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
